/**
 * English fallback messages. Loaded as the i18n fallback locale so any key
 * missing from `pa.ts` falls back here instead of rendering the raw key.
 *
 * Shape mirrors `pa.ts` exactly — keep them aligned when adding new keys.
 */
export default {
  splash: {
    badge: 'Authorised Field Use',
    titleWord1: 'Wattr',
    titleWord2: 'Survey',
    subtitle: 'Field intake for pump installations & audits',
    step1: 'Scan IMEI',
    step2: 'Capture Details',
    step3: 'Upload Photos',
    chips: {
      offline: 'Offline-ready',
      secure: 'Secure intake',
      sync: 'Real-time sync',
    },
    cta: 'Begin Survey',
    installer: 'Installer login (installation & field test)',
    foot: 'Powered by Wattr · wattr.ai',
  },
  whatNext: {
    newTitle: 'Start a new survey',
    newSub: 'Survey a pump you have not sent yet',
    pastTitle: 'Surveys you have sent',
    pastSub: 'Check what you sent from this number',
    pastNone: 'You have not sent any yet',
    needsFix: '{n} survey needs your correction | {n} surveys need your correction',
    back: 'Back',
  },
  ui: {
    appTitle: 'Wattr Survey',
    appSubtitle: 'Field intake · {n}-step',
    online: 'Online',
    offline: 'Offline',
    syncing: 'Syncing…',
    unsynced: '{n} unsynced',
    syncToastSent: '{n} survey{plural} synced',
    syncToastNone: 'Nothing to sync',
    syncToastBlocked: "{n} draft(s) can't be sent — tap the warning to review",
    blockedPill: '{n} needs attention',
    blockedTitle: 'Drafts that need you',
    blockedBody: 'These were retried until we gave up. They will not send on their own — try again once the cause is fixed, or discard them.',
    blockedRetry: 'Try again',
    blockedDiscard: 'Discard',
    blockedDiscarded: 'Draft discarded',
    blockedAttempts: 'Tried {n} times',
    blockedUnknownError: 'Unknown error',
    syncToastOffline: 'Offline — will sync when the network is back',
    syncToastFailed: 'Sync failed — will retry',
    syncToastFailedReason: 'Sync failed: {reason}',
    back: 'Back',
    continue: 'Continue',
    submit: 'Submit Survey',
    selectPlaceholder: 'Select…',
    selectYearPlaceholder: 'Select year…',
    selectCropPlaceholder: 'Select a crop…',
    selectAfterCrop: 'Choose the crop first',
    typeVarietyName: 'Type variety name',
    yes: 'Yes',
    no: 'No',
    other: 'Other',
    otherCropOption: 'Other (not in list)',
    otherCropName: 'Crop name',
    otherVarietyName: 'Variety name',
    otherCropNamePlaceholder: 'Type crop name in English',
    otherVarietyNamePlaceholder: 'Type variety name in English',
    addAnotherCrop: 'Add another crop',
    removeCrop: 'Remove this crop',
    noVarieties: 'No named varieties for this crop — selection saved.',
    varietiesHeader: 'Variety (select one)',
    requiredMark: '*',
    optionalTag: 'Optional',
    otherNotInList: 'Other (not in list)',
    otherSpecifyPlaceholder: 'Type the {label}',
    otherBackToList: '← Pick from list',
    reset: 'Reset',
    cancel: 'Cancel',
    startAnother: 'Start another survey',
    surveySaved: 'Survey saved',
    autofilledHint: 'Auto-filled from device record · edit if needed',
    autofilledBanner: 'Auto-filled {n} field{plural} from device record.',
    submitBannerSingle: '{msg}',
    submitBannerMulti: 'Fill in {n} required field{plural} before continuing.',
    submitGate: 'Please complete the highlighted fields before submitting.',
    submitSavedSent: 'Submission saved and sent.',
    submitSavedRetry: 'Saved locally. Will retry when the network is back.',
    submitSaved: 'Submission saved.',
    submitOffline: 'Offline — saved locally. We will sync when you reconnect.',
    submitFailFallback: 'Could not save the draft. Try again.',
    purposeLockedHint: 'Repair & Installation flows are coming soon. Audit is the only option for now.',
    searchablePlaceholder: 'Search {label}…',
    searchableStrictPlaceholder: 'Tap to pick {label}',
    searchableSearching: 'Searching…',
    searchableNoMatches: 'No matches yet — keep typing.',
    photoCapturing: 'Capturing…',
    photoAdd: 'Add',
    cameraDeniedText: 'Camera access needed to scan IMEI codes.',
    cameraOpenSettings: 'Open Settings',
    scanLabel: 'Scan',
    scanCancel: 'Cancel',
    scanHint: 'Hold steady — point the camera at the QR or barcode',
    scanZoomHint: 'Small QR? Pinch or use the slider to zoom in.',
    scanPhotoLabel: '🖼️ Scan tiny QR from photo',
    scanPhotoScanning: 'Reading photo…',
    scanNoCode: 'No QR/IMEI found. Fill the frame with the code, hold steady, and try again.',
    scanTorchOn: '🔦 Off',
    scanTorchOff: '🔦 Torch',
    scanWebOnly: 'Scan only available in the native app. Type the IMEI manually.',
    scanPermDenied: 'Camera permission was denied — open Settings to enable.',
    headerThemeAria: 'Theme: {choice}. Tap to switch.',
    headerLangAria: 'Current language: English. Tap to switch to ਪੰਜਾਬੀ.',
    langShortPa: 'ਪੰ',
    langShortEn: 'EN',
    syncRetryTitle: 'Tap to retry',
    syncLastTitle: 'Last synced {time}',
    resetTitle: 'Reset survey state?',
    resetBody: 'This clears every field on this device — IMEI, location, farmer details, pump & irrigation answers — and returns you to step 1.',
    resetWarnDrafts: 'You have {n} unsynced draft{plural} on this device.',
    resetDiscardToggle: 'Also discard {n} unsynced draft{plural}',
    resetDiscardHint: 'Cannot be undone. Leave off to keep them queued.',
    resetConfirm: 'Reset state',
    resetting: 'Resetting…',
    resetToastCleared: 'State reset · {n} pending draft{plural} discarded',
    resetToastReady: 'State reset · ready for a fresh survey',
    consentTitle: 'Help us audit submissions?',
    consentLead: 'We can attach a small context blob to each submission you make:',
    consentItem1Strong: 'Device model + Android version',
    consentItem1: ' — helps debug app crashes by device class.',
    consentItem2Strong: 'GPS coordinates at submit time',
    consentItem2: ' — fraud signal if 50 surveys come from one bench.',
    consentItem3Strong: 'Network type + battery',
    consentItem3: ' — explains failed syncs.',
    consentItem4Strong: 'Time-to-fill',
    consentItem4: ' — flags impossibly fast submissions.',
    consentFoot: 'We never read your phone IMEI, contacts, or installed apps. You can deny — submissions still go through.',
    consentDeny: 'Not now',
    consentAllow: 'Allow',
  },
  mySurveys: {
    fixNow: 'Fix now',
    title: 'My Surveys',
    subtitle: 'Enter your phone number to view all surveys you have submitted. Surveys marked for re-survey can be re-opened and edited.',
    phonePlaceholder: '10-digit phone number',
    lookup: 'Look up',
    loading: 'Looking up…',
    opening: 'Opening…',
    edit: 'Edit',
    empty: 'No surveys found for this number yet.',
    close: 'Close',
    noLocation: 'Location not set',
    viewingFor: 'Showing surveys submitted from {phone}',
    changeNumber: 'Change number',
    enterPhonePrompt: 'Enter your phone number to see your previous surveys.',
    onlyResurveyEditable: 'Any survey that hasn\'t been approved can be edited.',
    errInvalidPhone: 'Phone number must be exactly 10 digits.',
    errFetch: 'Could not load your surveys. Please retry shortly.',
    errEditLocked: 'This survey can no longer be edited.',
    errEditFetch: 'Could not open the draft. Please retry.',
    status: {
      pending: 'Processing',
      review: 'In review',
      approved: 'Approved',
      resurvey: 'Resurvey requested',
    },
  },
  installer: {
    login: {
      badge: 'Installer Access',
      title: 'Installer Sign-in',
      subtitle: 'Sign in to install and field-test pumps.',
      personaAria: 'Choose installer type',
      fcm: 'FCM',
      contract: 'Contract Worker',
      usernameLabel: 'Username',
      usernamePlaceholder: 'Your FCM username',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Your FCM password',
      phoneLabel: 'Phone Number',
      phonePlaceholder: '10-digit mobile',
      contractHint: 'Contract workers sign in with their phone number only — no password needed.',
      signIn: 'Sign In',
      signingIn: 'Signing in…',
      errFcmRequired: 'Enter both your username and password.',
      errPhoneInvalid: 'Phone number must be exactly 10 digits.',
      foot: 'Powered by Wattr · wattr.ai',
    },
    home: {
      title: 'Installer Home',
      comingSoon: 'Installer Home — coming soon',
      welcome: 'What would you like to do?',
      persona: 'Persona',
      personaFcm: 'FCM',
      personaContract: 'Contract Worker',
      logout: 'Sign out',
    },
    nav: {
      pending: 'Pending Field Tests',
      pendingDesc: 'Pumps awaiting a field test',
      newInstallation: 'New Installation',
      newInstallationDesc: 'Register a farmer, pump & device',
      fieldTest: 'Field Test',
      checklist: 'Checklist',
      contractUnavailable: 'Not available for contract workers',
    },
  },
  validation: {
    required: '{label} is required.',
    digitsOnly: '{label} must contain digits only.',
    exactLength: '{label} must be exactly {n} digits.',
    patternInvalid: '{label} format is invalid.',
    min: '{label} must be ≥ {n}.',
    max: '{label} must be ≤ {n}.',
    imeiAntiBlank: 'IMEI must be {n} digits.',
    farmerAntiBlank: 'Add the farmer name or contact number before submitting.',
    missingPhotos: 'Missing photos: {names}.',
    varietyRequired: 'Choose the variety for each crop in {label}.',
  },
  correction: {
    title: '{n} field(s) need fixing',
    body: 'Our team checked your survey. Only the highlighted fields need changing — everything else is already confirmed.',
    jump: 'Take me there',
    fieldNeedsFix: 'Needs fixing',
    fieldConfirmed: 'Confirmed by our team',
    needsFixBadge: '{n} to fix',
  },
  steps: {
    surveyor: {
      title: 'Surveyor Identity',
      subtitle: 'Tell us who is collecting this data — needed for traceability.',
    },
    previous_surveys: {
      title: 'What do you want to do?',
      subtitle: 'Pick one.',
    },
    purpose: {
      title: 'Survey Purpose',
      subtitle: 'Repair & Installation flows are coming soon. Audit is the only option for now.',
    },
    installation_option: {
      title: 'Installation Option',
      subtitle: 'Survey is for a new / pre-installation. Audit is for an existing installed device.',
    },
    device: {
      title: 'Device Identification',
      subtitle: 'Scan or type the 15-digit IMEI from the device label.',
    },
    location: {
      title: 'Location',
      subtitle: 'State auto-fills from the device; pick District & Block from the list.',
    },
    farmer: {
      title: 'Farmer Details',
    },
    pump: {
      title: 'Pump & Feeder',
    },
    discharge: {
      title: 'Pump Discharge',
      subtitle: 'Discharge can only be measured while the pump is running. If electricity is not available right now, answer No and we will skip the measurement.',
    },
    irrigation: {
      title: 'Irrigation Practices',
    },
    crops: {
      title: 'Crops Grown',
      subtitle: 'Kharif is required; Rabi & Zaid are optional (leave blank if single-cropping).',
    },
    photos: {
      title: 'Photos',
      subtitle: 'Capture the required slots — the checklist matches the visit purpose.',
    },
  },
  fields: {
    surveyor_name: {
      label: 'Your Name (optional)',
      placeholder: 'Full name (optional)',
      help: 'Optional — saved on this device after first entry.',
    },
    surveyor_contact: {
      label: 'Your Phone Number',
      placeholder: '10-digit mobile',
      help: 'Your phone number is your surveyor ID — the next screen shows the surveys you have already sent from it.',
    },
    surveyor_employee_id: {
      label: 'Employee / Vendor ID (optional)',
      placeholder: 'If your org issued one',
    },
    survey_purpose: {
      label: 'Purpose of Visit',
      help: 'Installation = first-time setup · Repair = fixing a fault · Audit = inspection only.',
      options: {
        installation: 'Installation',
        repair: 'Repair',
        audit: 'Audit',
      },
    },
    survey_mode: {
      label: 'Survey Type',
      options: {
        with_existing_device: 'With existing device',
        new_pre_installation: 'New / Pre-installation',
      },
    },
    installation_option: {
      label: 'Installation Option',
      help: 'Survey = new or pre-installation · Audit = inspect/repair an already-installed device.',
      options: {
        survey: 'Survey (new / pre-installation)',
        audit: 'Audit (existing device)',
      },
    },
    tenant_id: {
      label: 'Tenant / Organization',
      help: 'Pick the tenant this pump belongs to — the farmer login is created under it.',
    },
    imei_number: {
      label: 'IMEI Number',
      placeholder: '15-digit IMEI',
      help: 'Tap the scan button to read the QR/barcode on the device.',
    },
    state: {
      label: 'State / Union Territory',
      help: 'Auto-fetched from the device record; otherwise search to pick.',
    },
    district: {
      label: 'District',
      help: 'Auto-fetched if mapped; otherwise pick from the list.',
    },
    block: {
      label: 'Block',
      help: 'Auto-fetched if mapped; otherwise pick from the list.',
    },
    village: { label: 'Village' },
    farmer_name: { label: 'Farmer Name' },
    farmer_father_name: { label: "Farmer's Father's Name" },
    farmer_contact_number: {
      label: 'Farmer Contact Number',
      placeholder: '10-digit mobile',
    },
    farmer_caste: {
      label: 'Farmer Caste',
      options: { general: 'General', obc: 'OBC', sc: 'SC', st: 'ST' },
    },
    feeder_name: {
      label: 'Feeder Name',
      help: 'Auto-fetched when mapped; otherwise pick from the list or type the name.',
    },
    installation_date: {
      label: 'Installation Date',
      help: 'Auto-fetched when available; otherwise pick a date.',
    },
    year_of_pump_installation: { label: 'Year of Pump Installation' },
    pump_type: {
      label: 'Pump Type',
      options: { centrifugal: 'Centrifugal', submersible: 'Submersible' },
    },
    starter_button_type: {
      label: 'Starter Type',
      help: 'Number of buttons on the motor starter panel — check the physical starter, do not assume.',
      options: { '2': '2-Starter (2-button)', '3': '3-Starter (3-button)' },
    },
    horse_power: { label: 'Horse Power' },
    depth_of_source: { label: 'Depth of Water Source' },
    outlet_pipe_diameter_in: {
      label: 'Diameter of Outlet Pipe (inches)',
      help: 'Inside diameter of the outlet pipe in inches.',
    },
    vertical_distance_in: {
      label: 'Vertical Distance (inches)',
      help: 'Vertical drop of the water arc from the pipe lip.',
    },
    field_boundary: {
      label: 'Field Boundary (tap GPS at each corner)',
      help: 'Stand at each corner of the field and capture a GPS reading — four corners are joined into the field polygon.',
      hint: 'Stand on a corner and tap "Capture corner" — clockwise or anti-clockwise both work.',
      captureCorner: 'Capture corner {n}/{total}',
      capturing: 'Locking GPS…',
      recapture: 'Recapture',
      cancel: 'Cancel',
      remap: 'Re-map',
      distance: 'Perimeter',
      area: 'Area',
      mapHint: 'Map appears once you start capturing corners',
      errorPermission: 'Location permission denied — enable it in Settings.',
      errorUnavailable: 'GPS unavailable on this device.',
      errorTimeout: 'GPS timed out — please retry.',
      addField: 'Add another field',
      fieldN: 'Field {n}',
      totalArea: 'Total area',
      removeField: 'Remove',
    },
    pump_location: {
      label: 'Pump Live GPS Location',
      help: 'Capture the pump\'s real location via GPS before taking photos — required.',
      capture: 'Capture current location',
      capturing: 'Locking GPS…',
      recapture: 'Re-capture',
      captured: 'GPS location captured',
      accuracy: 'Accuracy: ±{m} m',
      photosLocked: 'Capture the GPS location above to unlock photos.',
      errorPermission: 'Location permission denied — enable it in Settings.',
      errorUnavailable: 'GPS unavailable — step outside and retry.',
      errorTimeout: 'GPS timed out — please retry.',
    },
    pump_discharge: {
      label: 'Pump Discharge (auto-calculated)',
      help: 'Auto-calculated in L/sec from outlet pipe diameter + vertical distance using Q = 0.05963 × D × D × V.',
    },
    pump_runtime_hours: {
      label: 'Pump Runtime (Hours)',
      help: 'Average daily run time.',
    },
    land_type: {
      label: 'Agricultural Land Type',
      options: { owned: 'Owned', rented: 'Rented', leased: 'Leased' },
    },
    pump_coverage_acres: { label: 'Pump Coverage (acres)' },
    electricity_at_visit: {
      label: 'Is electricity available at the site right now? (your visual check)',
      help: 'Look at the meter / main switch yourself — do not rely on the device. We use your answer to decide whether to ask for discharge measurements.',
    },
    discharge_method: {
      label: 'How will you measure discharge?',
      help: 'Flowmeter: read the discharge straight off the meter in L/s. Trajectory: we calculate it from the pipe diameter and water-arc drop.',
      options: { flowmeter: 'Flowmeter', trajectory: 'Trajectory method' },
    },
    flowmeter_discharge_lps: {
      label: 'Water Discharge (L/s)',
      help: 'Read the discharge directly from the flowmeter, in litres per second.',
    },
    has_issues: { label: 'Has the pump had issues?' },
    issues_faced: { label: 'Issues Faced (tap all that apply)' },
    capacitor_available: { label: 'Capacitor Already Available' },
    canal_irrigated: { label: 'Canal Irrigated?' },
    canal_name: { label: 'Name of Canal' },
    canal_irrigation_days: { label: 'Days of Canal Irrigation per Season' },
    laser_levelling_done_year: { label: 'Laser Land Levelling Done (Year)' },
    laser_levelling_planned_year: { label: 'Laser Land Levelling Planned (Year)' },
    irrigations_per_acre: { label: 'Irrigations per Acre (main crop)' },
    kharif_crops: {
      label: 'Kharif (Jun – Oct)',
      help: 'Monsoon-season crops — rice, maize, cotton, pulses, fodder. Pick at least one crop and its variety.',
    },
    rabi_crops: {
      label: 'Rabi (Oct – Mar) · optional',
      help: 'Winter-season crops — wheat, mustard, gram, lentils.',
    },
    zaid_crops: {
      label: 'Zaid (Mar – Jun) · optional',
      help: 'Summer-season crops — fodder, summer pulses, vegetables.',
    },
    installation_photos: { label: 'Photos' },
  },
  issues: {
    bearing_failure_noise_vibration: 'Bearing failure (noise, vibration)',
    cavitation_noise_jerks_at_low_water_level: 'Cavitation (noise, jerks at low water level)',
    clogging_of_strainers_foot_valves_suction_line: 'Clogging of strainers/foot valves/suction line',
    corrosion_of_metallic_parts: 'Corrosion of metallic parts',
    dry_running_when_groundwater_falls: 'Dry-running when groundwater falls',
    electrical_faults_single_phasing: 'Electrical faults / single-phasing',
    impeller_wear_clogging_by_sand_silt_debris: 'Impeller wear / clogging by sand, silt, debris',
    motor_burn_out_coil_burn: 'Motor burn-out / coil burn',
    seal_gland_failure_or_leakage: 'Seal/gland failure or leakage',
    shaft_misalignment_bending: 'Shaft misalignment / bending',
  },
  crops: {
    rice_non_basmati: 'Rice (Non-Basmati)',
    rice_basmati: 'Rice (Basmati)',
    maize: 'Maize',
    bajra: 'Bajra (Pearl Millet)',
    cotton: 'Cotton',
    moong: 'Moong (Green Gram)',
    mash_urdbean: 'Mash / Black Gram (Urdbean, Mah ki Dal)',
    arhar: 'Arhar (Pigeon Pea)',
    soybean: 'Soybean',
    sugarcane: 'Sugarcane',
    groundnut: 'Groundnut',
    sesame: 'Sesame (Til)',
    sorghum: 'Sorghum (Jowar)',
    guinea_grass: 'Guinea Grass',
    guara: 'Guara (Cluster Bean)',
    cowpea: 'Cowpea',
    wheat: 'Wheat',
    mustard: 'Mustard / Raya',
    barley: 'Barley',
    gram_chickpea: 'Gram (Chickpea)',
    lentils: 'Lentils (Masoor)',
    peas: 'Peas',
    summer_moong: 'Summer Moong',
    summer_mash: 'Summer Mash',
    fodder_maize: 'Fodder Maize',
    fodder_bajra: 'Fodder Bajra',
    fodder_sorghum: 'Fodder Sorghum (Chari)',
    watermelon: 'Watermelon',
    muskmelon: 'Muskmelon',
    cucumber: 'Cucumber',
    bottle_gourd: 'Bottle Gourd',
    bitter_gourd: 'Bitter Gourd',
    pumpkin: 'Pumpkin',
    okra: 'Okra (Bhindi)',
    __other__: 'Other (not in list)',
  },
  photoSlots: {
    TUBEWELL: {
      label: 'Tubewell photo',
      helper: 'Wide shot of the pump / borewell on site',
    },
    DEVICE_SETUP: {
      label: 'Device setup photo',
      helper: 'Wattr device installed with wiring visible',
    },
  },
  units: {
    feet: 'Feet',
    metre: 'Metre',
    lpm: 'LPM',
    cusec: 'Cusec',
    'm_hr': 'm³/hr',
    kl: 'kL',
    'l_sec': 'L/sec',
    acres: 'acres',
    sqm: 'm²',
  },
};
